/**
 * WS-C §3.2 — the shell's OWN login page (D-7′).
 *
 * ⛔ Not a bounce to the old console's `/login`. That was the void D-7, and the reason it is void
 * is that leaning on the old SPA is a dependency on a package scheduled for deletion — the exact
 * thing the fence forbids (spine §2 / §8.6).
 *
 * ⚠️ SCOPE NOTE FOR WHOEVER PICKS UP THE REST OF WS-C: this file covers the PASSWORD and PASSKEY
 * methods and the OAuth START navigation. `OAuthCallbackPage` (§3.3), `ChangePasswordPage` (§3.4)
 * and the §4 return-to stash are elsewhere.
 *
 * ✅ The `cp-auth-api` D-8′/D-21 redirect-target change IS NOW BUILT (#1221). See
 * `cp-auth-api/src/modules/oauth/oauth.service.ts` → `resolveUiCallbackBase`.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { KeyRound } from 'lucide-react';
import type { AuthCapabilities } from '../platform/auth/auth.api';
import { getApiBaseUrl } from '../platform/runtime-config';
import { stashGrafanaNext } from '../platform/auth/return-to';
import { displayMessage } from '../platform/errors';
import { AuthCardFrame, AuthField } from '../shell/AuthCardFrame';
import { Button } from '../shell/Button';

/**
 * ⛔ The fallback when the capabilities fetch fails.
 *
 * Rendering NOTHING would lock everyone out of the console on a transient blip. Password-only
 * degrades to the method that always exists — and it is deliberately NOT "enable everything",
 * which would render buttons for providers this deployment may not have configured.
 */
export const CAPABILITIES_FALLBACK: AuthCapabilities = {
  password: true,
  passkey: false,
  oauth: { google: false, github: false },
};

/**
 * Whether a rejection is the user closing the WebAuthn prompt.
 *
 * ⛔ A CANCELLATION IS NOT AN ERROR STATE. Rendering "authentication failed" when someone
 * deliberately dismissed the dialog trains people to distrust the control — and a passkey prompt
 * is dismissed by accident often enough that this is the common path, not the rare one. The
 * spec's own name for it is `NotAllowedError`; `AbortError` is what an `AbortSignal` teardown
 * surfaces as, and it is equally not a failure to report.
 */
export function isUserCancellation(error: unknown): boolean {
  return (
    error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

export interface LoginPageProps {
  /** `null` = the capabilities fetch has not settled. Renders the fallback set until it does. */
  readonly capabilities: AuthCapabilities | null;
  readonly onLogin: (email: string, password: string) => Promise<void>;
  readonly onPasskeyLogin: (email: string) => Promise<void>;
  /**
   * The one seam onto `window.location` for the OAuth hop.
   *
   * ⛔ A top-level NAVIGATION, not a `fetch`: the provider needs to own the browser for its
   * consent screen. ⛔ Tests inject a plain recording closure — `(u) => seen.push(u)` — never
   * `vi.fn()`.
   */
  readonly navigateExternal?: (url: string) => void;
}

function defaultNavigateExternal(url: string): void {
  window.location.assign(url);
}

export function LoginPage({
  capabilities,
  onLogin,
  onPasskeyLogin,
  navigateExternal = defaultNavigateExternal,
}: LoginPageProps): React.JSX.Element {
  const caps = capabilities ?? CAPABILITIES_FALLBACK;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * cp-auth-api sends a signed-out Grafana page load here as `/login?next=/grafana/...`. The path
   * goes into the return-to stash, so every sign-in method returns there (plan
   * 2026-10-07-grafana-host-and-login-redirect, decision 5). A `next` outside `/grafana` is ignored.
   */
  useEffect(() => {
    stashGrafanaNext();
  }, []);

  async function submitPassword(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onLogin(email, password);
    } catch (e: unknown) {
      // ⛔ The EMAIL FIELD IS NOT CLEARED. Retyping an address after a mistyped password is the
      // single most irritating thing a login form can do, and the password state is the only
      // thing worth discarding.
      setPassword('');
      setError(displayMessage(e, 'Sign-in failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitPasskey(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onPasskeyLogin(email);
    } catch (e: unknown) {
      // Cancellation returns to the idle form with NO message. See `isUserCancellation`.
      if (!isUserCancellation(e)) setError(displayMessage(e, 'Passkey sign-in failed.'));
    } finally {
      setBusy(false);
    }
  }

  function startOAuth(provider: 'google' | 'github'): void {
    /*
     * ⛔ `?ui=cp-admin` is an OPAQUE KEY, never a URL and never a path (D-21). `cp-auth-api`
     * resolves it against a server-derived exact-match table built from `SITE_ADMIN_URL`; an
     * unknown key is a 400, never a fallback. **No part of the caller's input reaches the
     * output**, which is what makes an open redirect UNREPRESENTABLE here rather than merely
     * defended against.
     *
     * ✅ THE SERVER SIDE IS BUILT (#1221) — `cp-auth-api`'s `resolveUiCallbackBase`, whose
     * `UI_CALLBACK_PATHS` map is the table described above. `cp-admin` resolves to
     * `${SITE_ADMIN_URL}/auth/oauth/callback`, and an unknown key is a 400 at `initiate`
     * (before the provider hop).
     *
     * ⚠️ TWO CLAIMS HERE WERE FALSIFIED BY THE WS-8 CUTOVER AND ARE CORRECTED ABOVE — recorded
     * rather than silently overwritten, because both read as reassuring and neither was:
     *   · the callback path was `/cp-admin/auth/oauth/callback`. `BASENAME` is `''` now, so the
     *     prefix is gone from the resolved URL. Nothing in the console constructs this path —
     *     the server owns it — which is exactly why a stale copy of it here was worth catching.
     *   · an ABSENT `?ui=` used to resolve to the OLD console, and this block called that
     *     "the other front-end keeps working unchanged". There is no other front-end.
     *     `DEFAULT_UI_KEY` is `'cp-admin'` now, so an absent key resolves HERE.
     *
     * ⛔ The `site-admin` map row is KEPT, and it is not vestigial: a login that STARTED before
     * the cutover carries `?ui=site-admin` in its state record and must still resolve on the way
     * back. Both rows now point at the same path, so that lands in this console.
     *
     * ⚠️ It is a SERVER-VERSION dependency, not a compile-time one: these buttons and
     * `cp-auth-api` must ship together, because the console cannot verify the server's table.
     */
    navigateExternal(`${getApiBaseUrl()}/v1/auth/oauth/${provider}?ui=cp-admin`);
  }

  /*
   * ⛔ **`AuthCardFrame` OWNS THE FRAME, THE CARD, THE `<h1>` AND THE ERROR BOX** — this file declared
   * all four itself, and the three pre-auth cards had already drifted apart on border colour, gap and
   * width. See `shell/AuthCardFrame.tsx`.
   *
   * ⚠️ **The `Shield` lozenge is gone, and so is the branding gap it papered over.** The title was a
   * 12px bold line beside an accent icon; it is now the deck's real page title, which is the same
   * treatment an authenticated page gets. ⛔ `Control Plane` stays HARD-CODED and unbranded — that is
   * `COMPONENT-LIBRARY.md` §4.7's ruling, not an oversight: the tenant is unknown before
   * authentication, and a brand hook here invites a call site that confirms an org's existence to an
   * unauthenticated caller. `CpBrandingProvider` lives inside the authenticated branch on purpose.
   */
  return (
    <AuthCardFrame
      title="Control Plane"
      /*
       * The failure goes to the frame's `notice`, which renders `role="alert"` for a `bad` verdict —
       * assertive is right here because it arrives in response to a submit the operator just made and
       * it blocks them. ⚠️ `undefined`, not `null`: the frame's prop is optional and the absent slot
       * renders nothing at all rather than an empty live region.
       */
      notice={error === null ? undefined : { verdict: 'bad', message: error }}
      testId="login"
    >
      {caps.password ? (
        <form className="flex flex-col gap-3" onSubmit={(e) => void submitPassword(e)}>
          <AuthField
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={setEmail}
            testId="login-email"
          />
          <AuthField
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            testId="login-password"
          />
          {/* ⛔ INSIDE the `<form>`, which is what makes Enter submit from either field. */}
          <Button type="submit" tone="primary" size="md" disabled={busy} data-testid="login-submit">
            Sign in
          </Button>
        </form>
      ) : null}

      {/*
        ⛔⛔ **RENDERED ONLY WHEN THE DEPLOYMENT OFFERS IT — and this is the ONE PLACE IN THE CONSOLE
        WHERE THE HIDE-VS-DISABLE RULE IS INVERTED.**

        In-app the rule is: never hide an action, show it disabled with a reason — because an operator
        who cannot see a control cannot tell a permission gap from a missing feature. ⛔ Pre-auth that
        reasoning does not apply and reverses: a passkey button on a deployment with no passkey support,
        or `Continue with Google` where no Google client is configured, is a **dead control** — it
        cannot be made to work by anyone, and "disabled: this deployment has no Google provider" tells
        an unauthenticated stranger about our configuration.

        ⚠️ A converter applying the in-app rule here renders sign-in buttons for providers that do not
        exist. Both halves of the rule are correct; this is the half that belongs on this screen.
      */}
      {caps.passkey ? (
        <Button size="md" disabled={busy} onClick={() => void submitPasskey()} data-testid="login-passkey">
          <KeyRound className="h-4 w-4" aria-hidden />
          Use a passkey
        </Button>
      ) : null}

      {caps.oauth.google ? (
        <Button size="md" onClick={() => startOAuth('google')} data-testid="login-google">
          Continue with Google
        </Button>
      ) : null}

      {caps.oauth.github ? (
        <Button size="md" onClick={() => startOAuth('github')} data-testid="login-github">
          Continue with GitHub
        </Button>
      ) : null}
    </AuthCardFrame>
  );
}
